import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken, getCurrentSession, getCurrentUser } from '../auth';
import { useWorkspaceStore } from '../store/workspace';
import Loader from '../lib/loader/Loader';
import { envConfig } from '../config';
import { Logger } from '@elyx-code/common-ts-utils';
import { isUUID } from '@elyx-code/project-logic-tree';
import BetaAcknowledgeDialog from './dialogs/BetaAcknowledgeDialog';
import TutorialVideoDialog from './dialogs/TutorialVideoDialog';
import AIDisabledDialog from './dialogs/AIDisabledDialog';

const WorkspaceLayout: React.FC = () => {
	// We keep the param named workspaceId from the router, but treat it as a generic identifier
	const { workspaceId: workspaceIdentifier } = useParams<{
		workspaceId: string;
	}>();
	// Get current path
	const currentPath = window.location.pathname;
	const [loading, setLoading] = useState(true);
	const setActiveWorkspace = useWorkspaceStore(
		(state) => state.setActiveWorkspace,
	);
	const navigate = useNavigate();

	const [userId, setUserId] = useState<string | null>(null);

	const [showBetaDialog, setShowBetaDialog] = useState(false);
	const [showTutorialDialog, setShowTutorialDialog] = useState(false);
	const [showAIDisabledDialog, setShowAIDisabledDialog] = useState(false);

	const handleAcknowledgeBeta = () => {
		if (userId) localStorage.setItem(`elyx_beta_acknowledged_${userId}`, 'true');
		setShowBetaDialog(false);
	};

	const handleAcknowledgeTutorial = () => {
		if (userId) localStorage.setItem(`elyx_tutorial_acknowledged_${userId}`, 'true');
		setShowTutorialDialog(false);
	};

	const handleAcknowledgeAI = () => {
		if (userId) localStorage.setItem(`elyx_ai_disabled_acknowledged_${userId}`, 'true');
		setShowAIDisabledDialog(false);
	};

	const pendingNoticesCount = [
		showBetaDialog,
		showTutorialDialog,
		showAIDisabledDialog,
	].filter(Boolean).length;

	const stepText = pendingNoticesCount > 1 
		? `1 of ${pendingNoticesCount} pending notices`
		: pendingNoticesCount === 1 
		? '1 of 1 pending notice' 
		: undefined;

	useEffect(() => {
		const fetchWorkspace = async () => {
			if (!workspaceIdentifier) return;

			// Auth gate. This layout wraps every `/ws/:workspaceId/*` route
			// (gallery, editor), so it's the single chokepoint where an
			// unauthenticated user lands before any of those pages get to
			// render. Without an explicit check here, an unauth user would
			// briefly flash the page chrome (or worse, the page itself) while
			// the workspace API call below failed, fell through the catch,
			// navigated to '/', and let LegacyRouteRedirector finally bounce
			// them to /auth/sign-in. We do the check up-front while still
			// inside `loading=true`, so the existing centered Loader stays on
			// screen across the redirect — no flash. The full-page
			// `window.location.replace` (matching `getCurrentUserOrLogoutRedirect`)
			// guarantees we throw away any partially-mounted React state.
			const session = await getCurrentSession();
			if (!session) {
				const currentPath = window.location.pathname + window.location.search;
				const redirectTo = currentPath.startsWith('/auth')
					? '/gallery'
					: currentPath;
				window.location.replace(
					`/auth/sign-in?redirectTo=${encodeURIComponent(redirectTo)}`,
				);
				return;
			}

			const user = await getCurrentUser();
			if (user?.id) {
				setUserId(user.id);
				setShowBetaDialog(localStorage.getItem(`elyx_beta_acknowledged_${user.id}`) !== 'true');
				setShowTutorialDialog(localStorage.getItem(`elyx_tutorial_acknowledged_${user.id}`) !== 'true');
				setShowAIDisabledDialog(localStorage.getItem(`elyx_ai_disabled_acknowledged_${user.id}`) !== 'true');
			}

			// Check if the current active workspace matches either the ID or the Slug
			const currentActiveWorkspace =
				useWorkspaceStore.getState().activeWorkspace;
			if (
				currentActiveWorkspace &&
				(currentActiveWorkspace.id === workspaceIdentifier ||
					currentActiveWorkspace.slug === workspaceIdentifier)
			) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				const token = await getAccessToken();

				// Determine if the URL param is a UUID or a slug
				const isId = isUUID(workspaceIdentifier);
				const queryParam = isId
					? `id=${workspaceIdentifier}`
					: `slug=${workspaceIdentifier}`;

				const response = await axios.get(
					`${envConfig.API_BASE_URL}/user-service/v1/workspaces?${queryParam}`,
					{
						headers: {
							Authorization: 'Bearer ' + token,
						},
					},
				);

				const workspaceData = response.data?.data;

				if (workspaceData) {
					setActiveWorkspace(workspaceData);
					if (workspaceData.slug !== workspaceIdentifier) {
						Logger.warn(
							`URL identifier (${workspaceIdentifier}) does not match workspace slug (${workspaceData.slug}), redirecting to URL with slug`,
						);
						// Replace the current path with the same path but using the workspace slug instead of the original identifier
						const newPath = currentPath.replace(
							workspaceIdentifier,
							workspaceData.slug,
						);
						navigate(newPath, { replace: true });
						return;
					}
				} else {
					Logger.warn('Workspace not found, redirecting to root');
					navigate('/', { replace: true });
				}
			} catch (error) {
				Logger.error(
					`Failed to load workspace data for identifier: ${workspaceIdentifier}`,
					error,
				);
				navigate('/', { replace: true });
			} finally {
				setLoading(false);
			}
		};

		fetchWorkspace();
	}, [workspaceIdentifier, setActiveWorkspace, navigate]);

	if (loading) {
		return (
			<div
				style={{
					display: 'flex',
					height: '100vh',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Loader size={60} color='primary' />
			</div>
		);
	}

	return (
		<>
			<Outlet />
			{showBetaDialog ? (
				<BetaAcknowledgeDialog onClose={handleAcknowledgeBeta} stepText={stepText} />
			) : showTutorialDialog ? (
				<TutorialVideoDialog onClose={handleAcknowledgeTutorial} stepText={stepText} />
			) : showAIDisabledDialog ? (
				<AIDisabledDialog onClose={handleAcknowledgeAI} stepText={stepText} />
			) : null}
		</>
	);
};

export default WorkspaceLayout;
